import { BarChart3, Filter, Plus, Search } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '../Common/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Common/select';
import { DomainSelector } from '../Common/DomainSelector';
import { UseCaseStatus, Domain } from '../../types';
import { useTranslation } from 'react-i18next';
import { getUseCaseStatusLabel } from './useCaseStatus';

export interface UseCasesActionsHeaderProps {
  /** Current search query */
  query: string;
  /** Current status filter */
  statusFilter: 'all' | UseCaseStatus;
  /** Current category filter */
  categoryFilter: string | 'all';
  /** Current domain filter ID */
  filterDomainId: string | null;
  /** Current value min filter */
  valueMin: number | '';
  /** Current value max filter */
  valueMax: number | '';
  /** Current complexity min filter */
  complexityMin: number | '';
  /** Current complexity max filter */
  complexityMax: number | '';
  /** Total number of use cases (for category filter display) */
  totalUseCases: number;
  /** Sorted categories with counts for filter buttons */
  sortedCategories: [string, number][];
  /** Available domains for domain filter */
  domains: Domain[];
  /** Callback when search query changes */
  onQueryChange: (query: string) => void;
  /** Callback when status filter changes */
  onStatusFilterChange: (status: 'all' | UseCaseStatus) => void;
  /** Callback when category filter changes */
  onCategoryFilterChange: (category: string | 'all') => void;
  /** Callback when domain filter changes */
  onDomainFilterChange: (domainId: string | null) => void;
  /** Callback when value min filter changes */
  onValueMinChange: (value: number | '') => void;
  /** Callback when value max filter changes */
  onValueMaxChange: (value: number | '') => void;
  /** Callback when complexity min filter changes */
  onComplexityMinChange: (value: number | '') => void;
  /** Callback when complexity max filter changes */
  onComplexityMaxChange: (value: number | '') => void;
  /** Callback when new use case button is clicked */
  onNewUseCaseClick: () => void;
  /** Number of selected use cases */
  selectedCount: number;
  /** Number of eligible use cases for prioritization */
  eligibleCount: number;
  /** Whether status update is in progress */
  isUpdatingStatus: boolean;
  /** Callback when mark as prioritized is clicked */
  onMarkAsPrioritized: () => void;
  /** Callback to select all eligible use cases */
  onSelectAllEligible: () => void;
  /** Callback to deselect all */
  onDeselectAll: () => void;
  /** Callback when the "View dashboard" button is clicked; hides the button when omitted */
  onViewDashboardClick?: () => void;
}

export const UseCasesActionsHeader: React.FC<UseCasesActionsHeaderProps> = ({
  query,
  statusFilter,
  categoryFilter,
  filterDomainId,
  valueMin,
  valueMax,
  complexityMin,
  complexityMax,
  totalUseCases,
  sortedCategories,
  domains,
  onQueryChange,
  onStatusFilterChange,
  onCategoryFilterChange,
  onDomainFilterChange,
  onValueMinChange,
  onValueMaxChange,
  onComplexityMinChange,
  onComplexityMaxChange,
  onNewUseCaseClick,
  selectedCount,
  eligibleCount,
  isUpdatingStatus,
  onMarkAsPrioritized,
  onSelectAllEligible,
  onDeselectAll,
  onViewDashboardClick,
}) => {
  const { t } = useTranslation();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  // Note: domains prop is kept in interface for consistency but DomainSelector fetches domains internally

  // Show selection bar when items are selected, otherwise show normal filters
  if (selectedCount > 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-foreground">
            {t('useCases.selection.selected', { count: selectedCount })}
          </span>
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAllEligible}
              disabled={isUpdatingStatus}
            >
              {t('useCases.selection.selectAllEligible', { count: eligibleCount })}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDeselectAll}
              disabled={isUpdatingStatus}
            >
              {t('useCases.selection.deselectAll')}
            </Button>
            <Button
              size="sm"
              onClick={onMarkAsPrioritized}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus
                ? t('useCases.selection.updating')
                : t('useCases.selection.markAsPrioritized')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 md:min-w-[320px] relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-muted-foreground" />
          </div>
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={t('useCases.filters.searchPlaceholder')}
            className="w-full pl-10 pr-3 py-2 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
        <div className="w-full md:w-[180px] shrink-0">
          <Select
            value={statusFilter}
            onValueChange={(v) => onStatusFilterChange(v as 'all' | UseCaseStatus)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('useCases.filters.status.all')}</SelectItem>
              <SelectItem value="Identified">{getUseCaseStatusLabel('Identified', t)}</SelectItem>
              <SelectItem value="In Progress">{getUseCaseStatusLabel('In Progress', t)}</SelectItem>
              <SelectItem value="Qualified">{getUseCaseStatusLabel('Qualified', t)}</SelectItem>
              <SelectItem value="Prioritized">{getUseCaseStatusLabel('Prioritized', t)}</SelectItem>
              <SelectItem value="Completed">{getUseCaseStatusLabel('Completed', t)}</SelectItem>
              <SelectItem value="Abandoned">{getUseCaseStatusLabel('Abandoned', t)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full md:w-[180px] shrink-0">
          <DomainSelector
            selectedDomainId={filterDomainId}
            onDomainChange={(d) => onDomainFilterChange(d ? d.id : null)}
          />
        </div>
        <Button variant="outline" className="flex items-center gap-2 shrink-0" onClick={() => setIsFiltersOpen((v) => !v)}>
          <Filter className="h-4 w-4" />
          {t('global.filter')}
        </Button>
        {onViewDashboardClick && (
          <Button variant="outline" className="flex items-center gap-2 shrink-0" onClick={onViewDashboardClick}>
            <BarChart3 className="h-4 w-4" />
            {t('useCases.list.viewDashboard', 'Voir le tableau de bord')}
          </Button>
        )}
        <Button className="flex items-center gap-2 shrink-0" onClick={onNewUseCaseClick}>
          <Plus className="h-4 w-4" />
          {t('useCases.actions.new')}
        </Button>
      </div>
      {isFiltersOpen && (
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <div className="mb-1 text-sm font-medium">{t('global.category')}</div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  categoryFilter === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
                onClick={() => onCategoryFilterChange('all')}
              >
                {t('global.all')}
                {totalUseCases ? <span className="ml-1 text-[10px] opacity-80">({totalUseCases})</span> : null}
              </button>
              {sortedCategories.map(([cat, count]) => (
                <button
                  key={cat}
                  type="button"
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    categoryFilter === cat
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  onClick={() => onCategoryFilterChange(cat)}
                >
                  {cat}
                  <span className="ml-1 text-[10px] opacity-80">({count})</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">{t('useCases.filters.businessValue')}</div>
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={10}
                value={valueMin}
                onChange={(e) => onValueMinChange(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-foreground"
              />
              <input
                type="number"
                min={1}
                max={10}
                value={valueMax}
                onChange={(e) => onValueMaxChange(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-foreground"
              />
            </div>
          </div>
          <div>
            <div className="mb-1 text-sm font-medium">{t('useCases.filters.technicalComplexity')}</div>
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                max={10}
                value={complexityMin}
                onChange={(e) => onComplexityMinChange(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-foreground"
              />
              <input
                type="number"
                min={1}
                max={10}
                value={complexityMax}
                onChange={(e) => onComplexityMaxChange(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-foreground"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
